import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, Users, Zap, Shield, Sparkles, 
  Calendar, Phone, DollarSign, ArrowRight, Brain, 
  Bot, Clock, Trophy, MessageSquare, FileText, Bell
} from 'lucide-react';

const WhyAshaBlog = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium border border-gray-200 mb-8">
            <Sparkles className="w-4 h-4" />
            <span>Why Asha?</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight tracking-tight">
            Why Asha is the Only AI Company That <br/>
            <span className="text-blue-600">Truly Automates Your Full Office</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
            Stop stitching together single-point solutions. Discover the power of a complete AI workforce.
          </p>

          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 font-medium">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-xs font-bold">A</div>
              <span>Asha Team</span>
            </div>
            <span>•</span>
            <span>Today</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>6 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="pb-24 px-6">
        <div className="container mx-auto max-w-4xl">
          
          {/* Introduction */}
          <div className="prose prose-lg prose-blue max-w-none mb-16">
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              In the rapidly evolving world of dental AI, it's easy to get lost in the noise. There are tools for phones, tools for insurance, and tools for scheduling. But at Asha, we believe you shouldn't need a dozen different vendors to run your practice.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We are the only company in the world that provides a <strong className="text-gray-900">full team of AI agents</strong> for both your front and back office. Here is why leading practices are switching to Asha.
            </p>
          </div>

          {/* Point 1: Full Team */}
          <div className="mb-20">
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-gray-200"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">1</span>
                The Only Full-Stack AI Workforce
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We don't just give you a "bot." We give you a complete digital staff. Our suite covers every critical role in your practice, seamlessly integrated.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Front Office Agents
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Receptionist:</strong> Handles calls 24/7 with human-like empathy.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Schedule Optimizer:</strong> Fills cancellations & gaps.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Hygiene Recall:</strong> Reactivates dormant patients.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Case Acceptance:</strong> Follows up on treatment plans.</span>
                    </li>
                     <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Patient Experience:</strong> Ensures satisfaction.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Marketing Coordinator:</strong> Nurtures new leads.</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Bot className="w-5 h-5 text-blue-600" />
                    Back Office Agents
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Insurance Verification:</strong> 300+ integrations.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Insurance Calls:</strong> Speaks to reps on hold.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Claim Submission:</strong> Generates & submits claims.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Claim Scrubbing:</strong> Reviews claims for errors.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>EOB Posting:</strong> Posts payments automatically.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Denial Management:</strong> Handles appeals.</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>AR & Collections:</strong> Tracks receivables.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Point 2: 15 Agents vs 1 */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">2</span>
              15 Agents, One Platform
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Other companies give you one AI agent for one specific task. That means you end up with 15 different logins, 15 different support teams, and 15 different bills. We give you <strong className="text-gray-900">15 AI agents in one unified platform</strong>, so you don't have to go to 15 different vendors.
            </p>
          </div>

          {/* Point 3: Sophistication */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">3</span>
              Unmatched Sophistication
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our agents aren't just simple chatbots. They are sophisticated AI workers capable of handling complex logic.
            </p>
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
              <p className="italic text-gray-700">
                "For example, our AI receptionist can follow intelligent, complex <strong className="text-blue-600">operatory-based scheduling rules</strong>. It understands the specific conditions around each time block—like provider availability, chair type, and procedure duration—to schedule the patient perfectly every time."
              </p>
            </div>
          </div>

          {/* Point 4: Insurance Verification */}
          <div className="mb-16">
             <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">4</span>
              Next-Level Insurance Verification
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We have the only insurance verification solution that can not only get breakdowns from <strong className="text-gray-900">300+ dental payer integrations</strong> but also <strong>call insurance companies, wait on hold, and speak to representatives</strong>. We get the hard-to-find details like procedure histories that other "automated" solutions simply can't access.
            </p>
          </div>

           {/* Point 5: Pricing & Comms Suite */}
          <div className="mb-16">
             <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">5</span>
              Unbeatable Pricing & Value
            </h2>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 text-blue-600 border border-gray-200 shadow-sm">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">We Price Match</h3>
                  <p className="text-gray-600">
                    We offer by far the most competitive pricing in the market. But the real value comes from what we include for free.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50/50 rounded-2xl p-8 border border-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Communication Suite & Marketing Suite Included
              </h3>
              <p className="text-gray-600 mb-6">
                Typically, you have to pay separate vendors for phones, fax, and messaging. With Asha, we bring it all together. When you sign up for our front office AI agent bundle, <strong className="text-gray-900">we include our entire Communication Suite & Marketing Suite (valued at $399/month) for FREE.</strong>
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-gray-700">VoIP Phone System</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <FileText className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-gray-700">e-Fax</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-gray-700">2-Way Patient Messaging</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <Bell className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-gray-700">Patient Reminders</span>
                </div>
              </div>
            </div>
          </div>

          {/* Point 6: Trust */}
           <div className="mb-16">
             <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">6</span>
              Trusted & Backed by the Best
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Trust is everything in healthcare. Asha has the strongest backing in the space, having raised over <strong className="text-gray-900">$4.4M from top institutional investors</strong>.
            </p>
            <div className="flex items-center gap-3 text-gray-700 bg-gray-50 border border-gray-200 p-4 rounded-xl inline-block">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Backed by Chief Medical Officers from major health systems.</span>
            </div>
          </div>

           {/* Point 7: Proven ROI */}
           <div className="mb-20">
             <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
               <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm font-bold">7</span>
              Proven Production Increases
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              Every agent we deploy is designed to drive production. We've rigorously proven this with practices across the country — driving measurable production increases in the first month.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">$7,000+</div>
                <div className="text-sm text-gray-500 uppercase tracking-wide font-bold">Addt'l Production / Mo</div>
                <div className="mt-2 text-gray-600">From AI Receptionist</div>
              </div>
              <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">$2,500+</div>
                <div className="text-sm text-gray-500 uppercase tracking-wide font-bold">Addt'l Production / Mo</div>
                <div className="mt-2 text-gray-600">From Hygiene Recall Specialist</div>
              </div>
            </div>
          </div>

        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            Ready to Automate Your Practice?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Join the only platform that offers a complete team of AI agents to drive your practice's growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Schedule Follow-up Call
              </Button>
            </a>
            <a href="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Start 14-Day Free Trial
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyAshaBlog;



























